import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { useRouter } from 'next/router';
import {
  updatePizzaOptions,
  updatePizzaWithUserSelections
} from '../../pizza/slice/pizza.slice';
import { userSelectionsSelector } from '../slice/userSelections.slice';
import useApplyDealRules from './useApplyDealRules';
import useDeepEffect from '@/common/useDeepEffect';
import { BuiltPizza, DisplayableItem, PizzaUserSelections } from '@/builders/pizza/dataTransformers/builderTypes';
import { AddableCartItemKinds } from '@/api/phdApiV2Client/request.types';
import { openModal } from '@/localization/actions';
import Routes from '@/router/routes';
import { dealDataOOSModalDetails, RETURN_TO_DEALS } from '@/common/Modal/modalDetails';
import { DEFAULT_NO_SAUCE_SELECTION } from '@/cart/EditBuilder/constants';

const composeUserSelectionsObject = (recipe: BuiltPizza): Partial<PizzaUserSelections> => ({
  finishers: recipe.finisher ? [recipe.finisher] : [],
  cheese: recipe.cheese,
  sauce: recipe?.sauce ?? DEFAULT_NO_SAUCE_SELECTION,
  crust: recipe.crust,
  size: recipe.size,
  specialInstructions: recipe.specialInstructions,
  meatToppings: recipe.meatToppings,
  veggieToppings: recipe.veggieToppings
});

const outOfStock = (options: DisplayableItem[]) => options
  .reduce((acc, current) => !!current.outOfStock && acc, true);

function usePizzaDataInRedux(builtPizzaForSelectedRecipeId?: BuiltPizza | null): boolean {

  const [openModalDispatched, setOpenModalDispatched] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const currentDealStep = useSelector(userSelectionsSelector.selectCurrentStep);

  const pizzaOptions = currentDealStep?.recipeOptions.find((pizza) => pizza.id === currentDealStep?.recipeId)?.pizzaBuilderOptions ?? null;

  const options = useApplyDealRules(pizzaOptions);
  const recipe = useSelector(userSelectionsSelector.selectCurrentStepDealRecipe);

  useDeepEffect(() => {
    if (options && !openModalDispatched) {
      const { sizes, crusts } = options;

      const outOfStockSizes = sizes && outOfStock(sizes);
      const outOfStockCrusts = crusts && outOfStock(crusts);

      if (outOfStockSizes || outOfStockCrusts) {
        dispatch(openModal({
          ...dealDataOOSModalDetails,
          cta: {
            text: RETURN_TO_DEALS,
            callback: () => {
              router.push(Routes.DEALS);
            }
          },
          hideCloseIcon: true
        }));
        setOpenModalDispatched(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useDeepEffect(() => {
    if (options) {
      if (builtPizzaForSelectedRecipeId && currentDealStep?.isSwap) {
        dispatch(updatePizzaOptions(options));
        const { crust, ...userSelections } = composeUserSelectionsObject(builtPizzaForSelectedRecipeId);
        if (!crust) {
          dispatch(updatePizzaWithUserSelections(userSelections));
        } else {
          dispatch(updatePizzaWithUserSelections({ crust, ...userSelections }));
        }
      } else{
          dispatch(updatePizzaOptions(options));
          if (recipe?.item && recipe?.item.kind === AddableCartItemKinds.BUILT_PIZZA) {
            const { crust, ...userSelections } = composeUserSelectionsObject(recipe.item);
            if (!crust) {
              dispatch(updatePizzaWithUserSelections(userSelections));
            } else {
              dispatch(updatePizzaWithUserSelections({ crust, ...userSelections }));
            }
          }
        }
    }
  }, [options, recipe, builtPizzaForSelectedRecipeId]);
  return false;
}

export default usePizzaDataInRedux;
